import { useRef, useState } from 'react';
import {
  Button, Spinner, Text, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { importEmployees, type ImportSummary } from '../../api/attendance.api';
import { attendanceErrorMessage } from '../../utils/attendance-error';
import { useT } from '../../i18n';
import { downloadEmployeesTemplate } from '../../utils/template-generator';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, paddingTop: tokens.spacingVerticalS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  scroll: { maxHeight: '40vh', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td: { padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
});

interface Props { open: boolean; tenantId: string; onClose: () => void; onImported: () => void }

/** Importa personal desde Excel: revisa (sin guardar), muestra errores por fila y confirma solo si no hay errores. */
export function EmployeeImportDialog({ open, tenantId, onClose, onImported }: Props) {
  const s = useStyles();
  const t = useT();
  const a = t.attendancePage;
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null); setSummary(null); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { reset(); onClose(); };

  const preview = useMutation({
    mutationFn: (f: File) => importEmployees(tenantId, f, true),
    onSuccess: setSummary,
    onError: (e) => { setSummary(null); setError(attendanceErrorMessage(e, a.errors, a.genericError)); },
  });
  const commit = useMutation({
    mutationFn: (f: File) => importEmployees(tenantId, f, false),
    onSuccess: (r) => { setSummary(r); setFile(null); onImported(); },
    onError: (e) => setError(attendanceErrorMessage(e, a.errors, a.genericError)),
  });

  const pick = (f: File | undefined) => {
    setSummary(null); setError('');
    setFile(f ?? null);
    if (f) preview.mutate(f);
  };

  const canConfirm = !!file && !!summary && summary.dryRun && summary.errors.length === 0 && summary.toCreate > 0 && !commit.isPending;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) close(); }}>
      <DialogSurface style={{ maxWidth: 680 }}>
        <DialogBody>
          <DialogTitle>{a.importTitle}</DialogTitle>
          <DialogContent>
            <div className={s.body}>
              <Text size={200}>{a.importHint}</Text>
              <div className={s.actions}>
                <Button size="small" onClick={() => { void downloadEmployeesTemplate(); }}>{a.template}</Button>
                <Button appearance="primary" size="small" disabled={preview.isPending || commit.isPending} onClick={() => fileRef.current?.click()}>{a.chooseFile}</Button>
                <input
                  ref={fileRef} type="file" hidden accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  aria-label={a.chooseFile} onChange={(e) => pick(e.target.files?.[0])}
                />
                {file && <Text size={200}>{file.name}</Text>}
                {preview.isPending && <Spinner size="tiny" label={a.validating} />}
              </div>

              {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}

              {summary && summary.errors.length > 0 && (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <Text block weight="semibold">{a.errorsFound}</Text>
                    {summary.errors.map((e) => <Text key={`${e.row}-${e.message}`} block>{a.rowLabel(e.row)}: {e.message}</Text>)}
                  </MessageBarBody>
                </MessageBar>
              )}
              {summary && summary.warnings.length > 0 && (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <Text block weight="semibold">{a.warnings}</Text>
                    {summary.warnings.map((e) => <Text key={`${e.row}-${e.message}`} block>{a.rowLabel(e.row)}: {e.message}</Text>)}
                  </MessageBarBody>
                </MessageBar>
              )}

              {summary?.dryRun && summary.errors.length === 0 && (
                <>
                  <Text size={200} weight="semibold">{a.toCreate}: {summary.toCreate} · {a.alreadyExist}: {summary.alreadyExist}</Text>
                  {(summary.preview?.length ?? 0) > 0 && (
                    <div className={s.scroll}>
                      <table className={s.table}>
                        <thead><tr><th className={s.th}>{a.colRow}</th><th className={s.th}>{a.colName}</th><th className={s.th}>{a.employeeNumber}</th></tr></thead>
                        <tbody>
                          {summary.preview.map((r) => (
                            <tr key={r.row}><td className={s.td}>{r.row}</td><td className={s.td}>{r.name}</td><td className={s.td}>{r.employeeNumber ?? ''}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
              {summary && !summary.dryRun && (
                <MessageBar intent="success"><MessageBarBody>{a.importDone}: {summary.created ?? 0}</MessageBarBody></MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>{a.close}</Button>
            <Button appearance="primary" disabled={!canConfirm} icon={commit.isPending ? <Spinner size="tiny" /> : undefined} onClick={() => file && commit.mutate(file)}>
              {commit.isPending ? a.importing : a.confirmImport}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
